import { useState, useEffect, useRef } from 'react';
import cadenceGqlCall from '../utils/cadence-gql-call';
import { JOB_QUERY, JOBS_QUERY } from '../utils/cadence-queries';

const POLL_INTERVAL_BASE = 2000;
const POLL_INTERVAL_MAX = 30000;
const POLL_MAX_ERRORS = 3; // stop polling after 3 consecutive transient errors
const TERMINAL_STATUSES = ['COMPLETED', 'FAILED'];

/**
 * Polls a single background job by ID until it reaches a terminal status.
 * Returns { job, loading, isComplete, isFailed, error }.
 * Starts polling automatically when jobId is set, stops on unmount or terminal status.
 * Normal polling runs at a constant POLL_INTERVAL_BASE. Exponential backoff
 * (2s → 4s → 8s … capped at 30s) applies only during consecutive transient errors,
 * resetting as soon as a successful response is received.
 * Survives transient network errors (retries up to POLL_MAX_ERRORS consecutive times).
 */
export const useJobPoller = (jobId) => {
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const timeoutRef = useRef(null);
  const errorCountRef = useRef(0);

  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    errorCountRef.current = 0;

    const scheduleNext = (isError = false) => {
      const delay = isError
        ? Math.min(POLL_INTERVAL_BASE * Math.pow(2, errorCountRef.current), POLL_INTERVAL_MAX)
        : POLL_INTERVAL_BASE;
      timeoutRef.current = setTimeout(poll, delay);
    };

    const poll = async () => {
      try {
        const data = await cadenceGqlCall(JOB_QUERY, { id: String(jobId) });
        if (cancelled) return;
        errorCountRef.current = 0;
        const fetched = data?.job;
        if (fetched) {
          setJob(fetched);
          if (TERMINAL_STATUSES.includes(fetched.status)) {
            setLoading(false);
          } else {
            scheduleNext();
          }
        } else {
          // Job not found — permanent failure, stop polling
          setError('Job not found');
          setLoading(false);
        }
      } catch (err) {
        if (cancelled) return;
        errorCountRef.current += 1;
        if (errorCountRef.current >= POLL_MAX_ERRORS) {
          setError(err.message);
          setLoading(false);
        } else {
          // Transient error — keep polling with backoff
          scheduleNext(true);
        }
      }
    };

    poll();

    return () => {
      cancelled = true;
      clearTimeout(timeoutRef.current);
    };
  }, [jobId]);

  return {
    job,
    loading,
    error,
    isComplete: job?.status === 'COMPLETED',
    isFailed: job?.status === 'FAILED',
  };
};

/**
 * Fetches jobs for a cadence. Not a poller — one-time fetch, re-fetches when deps change.
 * Returns { jobs, loading, error, refetch }.
 *
 * Uses a generous limit since JOBS_QUERY supports server-side status filtering
 * (pass jobStatus to narrow results if the backend enum values are known).
 */
export const useCadenceJobs = (cadenceId, jobType = null) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fetchCount, setFetchCount] = useState(0);

  useEffect(() => {
    if (!cadenceId) return;
    setLoading(true);
    setError(null);

    const variables = { cadenceId: String(cadenceId), limit: 100 };
    if (jobType) variables.jobType = jobType;

    cadenceGqlCall(JOBS_QUERY, variables)
      .then((data) => {
        setJobs(data?.jobs || []);
      })
      .catch((err) => {
        setError(err.message);
        setJobs([]);
      })
      .finally(() => setLoading(false));
  }, [cadenceId, jobType, fetchCount]);

  const refetch = () => setFetchCount((c) => c + 1);

  return { jobs, loading, error, refetch };
};

export default useJobPoller;
